/**
 * Runs store — keeps a small history of simulator runs per flow, in
 * localStorage. Studio-only: in Connected mode the orchestrator records
 * production runs in ClickHouse, but the Studio simulator runs in the
 * browser, so we surface them in a collapsible panel for the merchant to
 * scan.
 *
 * Capped at LIMIT entries per flow to keep storage tight.
 */

import { create } from "zustand";

const STORAGE_PREFIX = "costplus:studio:v1:runs:";
const LIMIT = 50;

export interface RunEntry {
  id: string;
  flowId: string;
  /** ISO 8601. */
  timestamp: string;
  status: "success" | "error";
  /** Last node visited (or where the error was thrown). */
  endNodeId?: string;
  /** Number of nodes traversed. */
  pathLength?: number;
  errorCode?: string;
  errorMessage?: string;
}

interface RunsState {
  /** Currently visible flow's runs (loaded by loadForFlow). */
  flowId: string | null;
  runs: RunEntry[];
  /** Whether the side panel is expanded. */
  expanded: boolean;

  loadForFlow: (flowId: string) => void;
  recordRun: (entry: Omit<RunEntry, "id">) => void;
  clearForCurrentFlow: () => void;
  setExpanded: (expanded: boolean) => void;
}

function key(flowId: string): string {
  return `${STORAGE_PREFIX}${flowId}`;
}

function read(flowId: string): RunEntry[] {
  try {
    const raw = localStorage.getItem(key(flowId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(flowId: string, runs: RunEntry[]): void {
  try {
    localStorage.setItem(key(flowId), JSON.stringify(runs));
  } catch {
    /* quota exceeded — fail silently, history is best-effort */
  }
}

function uid(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export const useRunsStore = create<RunsState>((set, get) => ({
  flowId: null,
  runs: [],
  expanded: false,

  loadForFlow(flowId) {
    set({ flowId, runs: read(flowId) });
  },

  recordRun(entry) {
    const { flowId } = get();
    if (!flowId || flowId !== entry.flowId) return;
    const next: RunEntry = { id: uid(), ...entry };
    const updated = [next, ...read(flowId)].slice(0, LIMIT);
    write(flowId, updated);
    set({ runs: updated });
  },

  clearForCurrentFlow() {
    const { flowId } = get();
    if (!flowId) return;
    write(flowId, []);
    set({ runs: [] });
  },

  setExpanded(expanded) {
    set({ expanded });
  },
}));
